// Showcase (12 Showcase V1–V4, 12a home block, 12b S5/S6). The follow, vote and suggest forms post to the campaign Worker
// through the app proxy (/apps/9space/*), which emails a link to confirm the address; a follow or vote counts only then.
// Counts come from the Worker (D1 only) and show only above 0 (Rule 0). A referral code in the URL (?ref=) is kept for the
// device and rides on the cart as the attribute _ref, which the paid order carries back to the Worker.
import { refFromSearch, readReferral, attributeReferral } from './ns-referral.js';
import { countsUrl, loadShowcaseCounts, initShowcaseDiscovery } from './ns-showcase-discovery.js';
export { refFromSearch, readReferral, countsUrl };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SOURCE = /^[a-z0-9_-]{1,40}$/;
const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;

// The request key also travels in the query: the app proxy forwards the query, signed, whatever it does with headers.
export const keyedUrl = (endpoint, key) => `${endpoint}${endpoint.includes('?') ? '&' : '?'}key=${encodeURIComponent(key)}`;

// An ad's utm_source names the entry point (S6, the TikTok landing); otherwise the section's own source.
export function sourceFrom(search, fallback) {
  const utm = String(new URLSearchParams(search ?? '').get('utm_source') ?? '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
  return utm || (SOURCE.test(fallback ?? '') ? fallback : 'showcase');
}

export function leadPayload(mode, project, fields, { source, ref, locale }) {
  const email = String(fields.email ?? '').trim();
  if (!EMAIL.test(email) || email.length > 254) return { ok: false, error: 'email' };
  if (fields.consent !== true) return { ok: false, error: 'consent' };
  const body = { project: String(project), email, consent: true, source, locale: String(locale || 'en').slice(0, 12) };
  if (ref) body.ref = ref;
  if (mode === 'follow') {
    if (fields.option) body.option = String(fields.option).slice(0, 60);
    const guess = Number(fields.priceGuess);
    if (fields.priceGuess !== undefined && fields.priceGuess !== '' && Number.isInteger(guess) && guess >= 1) body.priceGuess = guess;
    // The launch-day gift tile the shopper picked (V3), a product handle; its radios sit in the card, outside the dialog.
    if (HANDLE.test(String(fields.gift ?? ''))) body.gift = String(fields.gift);
  }
  return { ok: true, body };
}

export function suggestPayload(fields, locale) {
  const name = String(fields.name ?? '').trim();
  const link = String(fields.link ?? '').trim();
  const why = String(fields.why ?? '').trim();
  const email = String(fields.email ?? '').trim();
  if (name.length < 2) return { ok: false, error: 'name' };
  if (link && !/^https?:\/\/[^\s]+$/i.test(link)) return { ok: false, error: 'link' };
  if (email && !EMAIL.test(email)) return { ok: false, error: 'email' };
  if (email && fields.consent !== true) return { ok: false, error: 'consent' };
  const body = { name, link, why, locale: String(locale || 'en').slice(0, 12) };
  if (email) Object.assign(body, { email, consent: true });
  return { ok: true, body };
}

// Only a 2xx answer that says saved is done. Showcase switched off on the Worker (503 not_ready, security review I5) is its own
// line, that nothing was saved; no proxy yet (404), a rate limit or a Worker error is the error line.
export const resultView = (status, json) => (status >= 200 && status < 300 && json?.saved === true ? 'done' : status === 503 && json?.error === 'not_ready' ? 'unavailable' : 'error');

export function countText(n, one, other) {
  const count = Number(n);
  if (!Number.isInteger(count) || count <= 0) return '';
  return String(count === 1 ? one : other).replace('{n}', String(count));
}

// The same line split around the number, so a tile can set it in bold (the Showcase Module's "**212** interested").
export function countParts(n, one, other) {
  const count = Number(n);
  if (!Number.isInteger(count) || count <= 0) return null;
  const template = String(count === 1 ? one : other);
  const at = template.indexOf('{n}');
  if (at < 0) return [template, '', ''];
  return [template.slice(0, at), String(count), template.slice(at + 3)];
}

export function progress(n, target) {
  const count = Number(n);
  const goal = Number(target);
  if (!(count > 0) || !(goal > 0)) return null;
  return { count, goal, percent: Math.min(100, Math.round((count / goal) * 100)) };
}

export const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (_, name) => String(values[name] ?? ''));

// V3 "Others are following": the other projects with the most confirmed follows, never one at 0 or without a count. Ties keep
// the page's order.
export function followingTop(handles, counts, limit = 4) {
  return [...new Set(handles)].map((handle, index) => ({ handle, followers: Number(counts?.[handle]?.followers), index }))
    .filter(row => Number.isSafeInteger(row.followers) && row.followers > 0)
    .sort((left, right) => right.followers - left.followers || left.index - right.index)
    .slice(0, limit)
    .map(({ handle, followers }) => ({ handle, followers }));
}

// One shared observation supplies both existing metrics and discovery. Unknown rows never become measured zeros.
const countHydrations = new WeakMap();
export async function hydrateShowcaseCounts(root, { fetcher = fetch, discovery } = {}) {
  const revision = (countHydrations.get(root) ?? 0) + 1;
  countHydrations.set(root, revision);
  const nodes = [...root.querySelectorAll('[data-ns-showcase-count], [data-ns-showcase-progress], [data-ns-showcase-ticker]')];
  const items = [...root.querySelectorAll('[data-ns-project-item]')];
  // V3: each launch-day gift's votes and the Others are following rows read the same answer.
  const extra = [...root.querySelectorAll('[data-ns-showcase-gift], [data-ns-showcase-following-row]')];
  const handles = [...nodes, ...items, ...extra].map(node => node.dataset.handle).filter(Boolean);
  const result = await loadShowcaseCounts(handles, { fetcher });
  if (countHydrations.get(root) !== revision) return result;
  for (const list of root.querySelectorAll('[data-ns-showcase-ticker-list]')) list.hidden = true;
  for (const node of nodes) {
    const row = result.counts[node.dataset.handle];
    const followers = row?.followers;
    node.hidden = true;
    if ('nsShowcaseCount' in node.dataset) {
      node.textContent = row ? countText(row[node.dataset.nsShowcaseCount], node.dataset.one, node.dataset.other) : '';
      node.hidden = node.textContent === '';
      const parts = row && countParts(row[node.dataset.nsShowcaseCount], node.dataset.one, node.dataset.other);
      if ('strong' in node.dataset && parts && parts[1]) {
        const number = document.createElement('b');
        number.textContent = parts[1];
        node.replaceChildren(parts[0], number, parts[2]);
      }
    } else if ('nsShowcaseProgress' in node.dataset) {
      const step = progress(followers, node.dataset.target);
      if (!step) continue;
      node.querySelector('[data-ns-showcase-fill]')?.style.setProperty('width', `${step.percent}%`);
      const bar = node.querySelector('[role="progressbar"]');
      bar?.setAttribute('aria-valuenow', String(step.count));
      const label = node.querySelector('[data-ns-showcase-progress-text]');
      if (label) label.textContent = fill(node.dataset.template, { n: step.count, target: step.goal });
      const percent = node.querySelector('[data-ns-showcase-percent]');
      if (percent) percent.textContent = `${step.percent}%`;
      node.hidden = false;
    } else if (followers > 0) {
      node.textContent = fill(followers === 1 ? node.dataset.one : node.dataset.other, { n: followers, title: node.dataset.title });
      node.hidden = false;
      node.closest('[data-ns-showcase-ticker-list]')?.removeAttribute('hidden');
    }
  }
  for (const node of root.querySelectorAll('[data-ns-showcase-gift]')) {
    node.textContent = countText(result.counts[node.dataset.handle]?.gifts?.[node.dataset.gift], node.dataset.one, node.dataset.other);
    node.hidden = node.textContent === '';
  }
  for (const section of root.querySelectorAll('[data-ns-showcase-following]')) {
    const rows = [...section.querySelectorAll('[data-ns-showcase-following-row]')];
    const top = followingTop(rows.map(row => row.dataset.handle), result.counts);
    const shown = top.map(({ handle, followers }) => {
      const row = rows.find(item => item.dataset.handle === handle);
      const count = row.querySelector('[data-ns-following-count]');
      if (count) count.textContent = fill(count.dataset.template, { n: followers });
      row.hidden = false;
      return row;
    });
    for (const row of rows) if (!shown.includes(row)) row.hidden = true;
    // Move the rendered rows into rank order; never build one.
    if (shown.length) shown[0].parentElement?.append(...shown, ...rows.filter(row => !shown.includes(row)));
    section.hidden = shown.length === 0;
  }
  discovery?.applyCounts(result);
  return result;
}

if (typeof document !== 'undefined') {
  const storage = () => { try { return window.localStorage; } catch { return null; } };
  // Reapply even a remembered code: a previous cart request may have failed, or this can be a new cart.
  const ref = refFromSearch(window.location.search) ?? readReferral(storage());
  if (ref) attributeReferral(storage(), ref, request => fetch(request.url, request.init));
  const currentRef = () => readReferral(storage()) ?? ref;

  const keys = new Map();
  let pending = null;
  async function post(endpoint, body) {
    const json = JSON.stringify(body);
    if (!keys.has(json)) keys.set(json, crypto.randomUUID());
    const key = keys.get(json);
    try {
      const response = await fetch(keyedUrl(endpoint, key), { method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: json });
      return resultView(response.status, await response.json().catch(() => null));
    } catch {
      return 'error';
    }
  }

  document.addEventListener('input', event => {
    const range = event.target instanceof HTMLInputElement && event.target.matches('[data-ns-guess]') ? event.target : null;
    const output = range?.closest('label')?.querySelector('[data-ns-guess-output]');
    if (output) output.textContent = fill(output.dataset.template, { price: range.value });
  });

  document.addEventListener('submit', async event => {
    const form = event.target instanceof HTMLFormElement && (event.target.matches('[data-ns-showcase-form]') || event.target.matches('[data-ns-suggest-form]')) ? event.target : null;
    if (!form) return;
    event.preventDefault();
    if (pending) return;
    const error = form.querySelector('[data-ns-follow-error]');
    const fields = Object.fromEntries(new FormData(form));
    fields.consent = form.elements.consent?.checked === true;
    const locale = document.documentElement.lang;
    const checked = form.matches('[data-ns-suggest-form]')
      ? suggestPayload(fields, locale)
      : leadPayload(form.dataset.nsShowcaseForm, form.dataset.project, fields, { source: sourceFrom(window.location.search, form.dataset.source), ref: currentRef(), locale });
    if (!checked.ok) {
      error.textContent = error.dataset[checked.error] ?? error.dataset.server;
      error.hidden = false;
      form.elements[checked.error]?.focus();
      return;
    }
    const submit = form.querySelector('[type="submit"]');
    submit.disabled = true;
    error.hidden = true;
    pending = post(form.dataset.endpoint, checked.body);
    const view = await pending;
    pending = null;
    submit.disabled = false;
    if (view !== 'done') { error.textContent = view === 'unavailable' ? error.dataset.unavailable : error.dataset.server; error.hidden = false; return; }
    const done = document.querySelector(`[data-ns-follow-done="${form.id}"]`);
    form.hidden = true;
    if (done) { done.hidden = false; done.focus(); }
  });

  // The home wall and the ad landing play their muted videos only in view, and never under reduced motion or data saver.
  function playInView() {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduce || navigator.connection?.saveData === true || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) entry.target.play()?.catch(() => {});
        else entry.target.pause();
      }
    }, { threshold: 0.5 });
    for (const video of document.querySelectorAll('video[data-ns-autoplay]')) observer.observe(video);
  }

  const start = () => { const discovery = initShowcaseDiscovery(document); hydrateShowcaseCounts(document, { discovery }); playInView(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
